/**
 * ModuleToolbar Component
 * Story 6.1: Implement Unified Toolbar Wrapper
 *
 * Unified toolbar that persists across module tabs (Applet, Grid, Dashboard).
 * AC-6.1.1: Toolbar persistence across tabs
 * AC-6.1.2: Standard toolbar icons in consistent order
 * AC-6.1.6: Accessibility requirements (role="toolbar", aria-label, etc.)
 */
import { type ReactNode } from 'react';
import {
  RefreshCw,
  Settings,
  Maximize2,
  LayoutGrid,
  Columns,
} from 'lucide-react';
import { IconUndo, IconRedo } from '../../views/grid/components/icons';
import { ToolbarButton } from './ToolbarButton';
import { useModuleToolbar, type ToolbarTabType } from './ModuleToolbarContext';
import { GridToolbarSection } from './GridToolbarSection';
import { AppletToolbarSection } from './AppletToolbarSection';
import { useToolbarShortcuts } from '../../hooks/useToolbarShortcuts';
import './ModuleToolbar.css';

interface ModuleToolbarProps {
  /** Module title to display on left side (Story 6.2 Task 9.1) */
  title?: string;
  /** Current active tab type for contextual icons */
  activeTabType: ToolbarTabType;
  /** Callback when refresh is triggered */
  onRefresh?: () => void;
  /** Callback when settings is triggered */
  onSettings?: () => void;
  /** Callback when present/fullscreen is triggered */
  onPresent?: () => void;
  /** Callback when view layout changes (Grid tab only) */
  onViewLayoutChange?: (layout: 'list' | 'grid' | 'card') => void;
  /** Current view layout (Grid tab only) */
  viewLayout?: 'list' | 'grid' | 'card';
  /** Additional content to render in toolbar */
  children?: ReactNode;
}

/**
 * ModuleToolbar - Unified toolbar wrapper for module tabs
 *
 * Layout:
 * [Undo][Redo][Refresh] | [Contextual...] | [ViewToggle][Split][Settings][Present]
 */
export function ModuleToolbar({
  title,
  activeTabType,
  onRefresh,
  onSettings,
  onPresent,
  onViewLayoutChange,
  viewLayout = 'grid',
  children,
}: ModuleToolbarProps) {
  const { onUndo, onRedo, canUndo, canRedo } = useModuleToolbar();

  // Register global shortcuts (AC-6.1.4)
  // This ensures shortcuts work even when focus is not in the grid
  useToolbarShortcuts({
    onUndo,
    onRedo,
    onRefresh,
    onSettings,
    onPresent,
  });

  // View toggle is only available for grid tab
  const showViewToggle = activeTabType === 'grid';

  return (
    <div
      role="toolbar"
      aria-label="Module toolbar"
      className="module-toolbar"
    >
      {/* Title - left aligned (Story 6.2 Task 9.1) */}
      {title && (
        <h1 className="font-bold text-lg tracking-tight text-foreground mr-4">
          {title}
        </h1>
      )}

      {/* Left Group: Undo, Redo, Refresh */}
      <div
        role="group"
        aria-label="History and refresh"
        data-testid="toolbar-left-group"
        className="toolbar-group"
      >
        <ToolbarButton
          icon={<IconUndo className="w-5 h-5" />}
          label="Undo"
          shortcut="Mod+Z"
          disabled={!canUndo}
          onClick={onUndo}
        />
        <ToolbarButton
          icon={<IconRedo className="w-5 h-5" />}
          label="Redo"
          shortcut="Mod+Shift+Z"
          disabled={!canRedo}
          onClick={onRedo}
        />
        <ToolbarButton
          icon={<RefreshCw className="w-5 h-5" />}
          label="Refresh"
          shortcut="Mod+R"
          onClick={onRefresh}
        />
      </div>

      {/* Separator */}
      <div role="separator" className="toolbar-separator" />

      {/* Contextual Icons based on active tab type */}
      <div className="toolbar-contextual flex-1">
        {activeTabType === 'grid' && <GridToolbarSection />}
        {/* Dashboard tab: No contextual icons - Edit Layout/Add Widget handled by DashboardCanvas sidebar */}
        {activeTabType === 'applet' && <AppletToolbarSection />}
        {children}
      </div>

      {/* Separator */}
      <div role="separator" className="toolbar-separator" />

      {/* Right Group: View Toggle, Split, Settings, Present */}
      <div
        role="group"
        aria-label="View and actions"
        data-testid="toolbar-right-group"
        className="toolbar-group"
      >
        {/* View Toggle - Only enabled for Grid tab */}
        <ToolbarButton
          icon={<LayoutGrid className="w-5 h-5" />}
          label="View toggle"
          disabled={!showViewToggle}
          onClick={() => {
            if (showViewToggle && onViewLayoutChange) {
              // Cycle through layouts: grid -> list -> card -> grid
              const layouts: Array<'list' | 'grid' | 'card'> = ['grid', 'list', 'card'];
              const currentIndex = layouts.indexOf(viewLayout);
              const nextIndex = (currentIndex + 1) % layouts.length;
              onViewLayoutChange(layouts[nextIndex]);
            }
          }}
        />

        {/* Split View - Deferred to Story 6-8 */}
        <ToolbarButton
          icon={<Columns className="w-5 h-5" />}
          label="Split view (coming soon)"
          shortcut="Mod+\"
          disabled={true}
          aria-disabled="true"
        />

        {/* Settings */}
        <ToolbarButton
          icon={<Settings className="w-5 h-5" />}
          label="Settings"
          shortcut="Mod+,"
          onClick={onSettings}
        />

        {/* Present/Fullscreen */}
        <ToolbarButton
          icon={<Maximize2 className="w-5 h-5" />}
          label="Present"
          shortcut="Mod+Enter"
          onClick={onPresent}
        />
      </div>
    </div>
  );
}

export default ModuleToolbar;
